import { useEffect, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Checkbox, Combobox, Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { createPluralHelperForI18nKey } from "app/pages/sales-intelligence/utils/strings";

import type { TeamListItem } from "../../types/teams.types";
import { areAllUsersInTeam } from "../../utils/areAllUsersInTeam";

const getAlreadyMemberTooltipKey = createPluralHelperForI18nKey("si.management.teams.users.bulk.already_member", true);

type AssignTeamOptionProps = {
    team: TeamListItem;
    isChecked: boolean;
    isDisabled: boolean;
    selectedUserIds: readonly number[];
    onToggleTeam: (teamId: number) => void;
    tooltipDismissSignal: number;
    dataAutomationPrefix: string;
};

export const AssignTeamOption = ({
    team,
    isChecked,
    isDisabled,
    selectedUserIds,
    onToggleTeam,
    tooltipDismissSignal,
    dataAutomationPrefix,
}: AssignTeamOptionProps) => {
    const { translate } = useTranslate();
    const isAlreadyMember = areAllUsersInTeam(team, selectedUserIds);
    const isTeamDisabled = isDisabled || isAlreadyMember;
    const teamName = team.name ?? "";

    const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (tooltipDismissSignal === 0) {
            return;
        }

        setIsOpen(false);
    }, [tooltipDismissSignal]);

    const optionLabel = (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            gap="12px"
            width="100%"
            opacity={isTeamDisabled ? 0.45 : 1}
        >
            <Combobox.OptionLabel>
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon["500"]}
                    as="span"
                >
                    {teamName}
                </Text>
            </Combobox.OptionLabel>
            <Checkbox selected={isChecked} />
        </Flex>
    );

    return (
        <Combobox.Option
            id={String(team.teamId)}
            textValue={teamName}
            isDisabled={isTeamDisabled}
            onClick={() => {
                if (!isTeamDisabled) {
                    onToggleTeam(team.teamId);
                }
            }}
            data-automation={`${dataAutomationPrefix}-row-${team.teamId}`}
        >
            {isAlreadyMember ? (
                <Tooltip
                    content={translate(getAlreadyMemberTooltipKey(selectedUserIds.length))}
                    open={isOpen}
                    onOpenChange={setIsOpen}
                >
                    {optionLabel}
                </Tooltip>
            ) : (
                optionLabel
            )}
        </Combobox.Option>
    );
};
